import React, { useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Checkbox } from './ui/checkbox';
import { 
  Pen, 
  Trash2, 
  Check, 
  X,
  User,
  Building2,
  AlertTriangle,
  ShieldAlert
} from 'lucide-react';

export function SignaturePad({ 
  title, 
  icon: Icon = Pen,
  existingSignature,
  existingName,
  existingDate,
  existingAcknowledged,
  onSave, 
  onDelete,
  disabled = false,
  requireAcknowledgement = false,
  defectCount = 0,
  criticalDefectCount = 0
}) {
  const sigCanvas = useRef(null);
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(existingName || '');
  const [acknowledged, setAcknowledged] = useState(false);

  const handleClear = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
    }
  };

  const handleSave = () => {
    if (sigCanvas.current && !sigCanvas.current.isEmpty() && name.trim()) {
      // Check if acknowledgement is required but not given
      if (requireAcknowledgement && defectCount > 0 && !acknowledged) {
        alert('Bitte bestätigen Sie die Kenntnisnahme der Mängel');
        return;
      }
      const signatureData = sigCanvas.current.toDataURL('image/png');
      onSave(signatureData, name.trim(), acknowledged);
      setIsEditing(false);
    }
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete();
    }
    setName('');
    setAcknowledged(false);
    setIsEditing(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('de-CH', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  // Show existing signature
  if (existingSignature && !isEditing) {
    return (
      <Card className="border-border/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Icon className="h-4 w-4 text-green-500" />
            {title}
            <Check className="h-4 w-4 text-green-500 ml-auto" />
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="bg-white rounded-lg p-2 border">
            <img 
              src={existingSignature} 
              alt="Unterschrift" 
              className="max-h-20 mx-auto"
            />
          </div>
          <div className="text-sm">
            <p className="font-medium">{existingName}</p>
            <p className="text-xs text-muted-foreground">{formatDate(existingDate)}</p>
          </div>
          
          {/* Show acknowledgement status for client */}
          {existingAcknowledged && (
            <div className="flex items-center gap-2 text-sm text-amber-500 bg-amber-500/10 p-2 rounded">
              <AlertTriangle className="h-4 w-4" />
              <span>Mängel zur Kenntnis genommen</span>
            </div>
          )}
          
          {!disabled && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={() => setIsEditing(true)}
              >
                <Pen className="h-3 w-3 mr-1" />
                Ändern
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-red-500 hover:text-red-600"
                onClick={handleDelete}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Show signature pad for editing/creating
  if (isEditing || !existingSignature) {
    return (
      <Card className="border-border/50 border-dashed">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Icon className="h-4 w-4 text-primary" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-2 relative z-10">
            <Label htmlFor={`name-${title}`} className="text-xs">Name</Label>
            <input
              id={`name-${title}`}
              type="text"
              placeholder="Name eingeben..."
              value={name}
              onChange={(e) => setName(e.target.value)}
              onTouchStart={(e) => e.stopPropagation()}
              className="flex h-10 w-full rounded-md border border-gray-300 bg-white text-gray-900 px-3 py-2 text-base shadow-sm transition-colors placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:cursor-not-allowed disabled:opacity-50"
              disabled={disabled}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              data-testid={`signature-name-${title.toLowerCase().replace(/\s+/g, '-')}`}
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-xs">Unterschrift</Label>
            <div 
              className="border rounded-lg bg-white overflow-hidden"
              style={{ touchAction: 'none' }}
            >
              <SignatureCanvas
                ref={sigCanvas}
                canvasProps={{
                  className: 'w-full h-32',
                  style: { width: '100%', height: '128px', touchAction: 'none' }
                }}
                backgroundColor="white"
              />
            </div>
          </div>
          
          {/* Acknowledgement checkbox for client when defects exist */}
          {requireAcknowledgement && defectCount > 0 && (
            <div className="space-y-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
              <div className="flex items-start gap-2 text-amber-600">
                <AlertTriangle className="h-5 w-5 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <p className="font-medium">Es wurden {defectCount} Mängel festgestellt</p>
                  {criticalDefectCount > 0 && (
                    <p className="text-red-500 font-medium mt-1 flex items-center gap-1">
                      <ShieldAlert className="h-4 w-4" />
                      Davon {criticalDefectCount} sicherheitsrelevant - sofortige Behebung erforderlich!
                    </p>
                  )}
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="acknowledge-defects"
                  checked={acknowledged}
                  onCheckedChange={setAcknowledged}
                  className="mt-0.5"
                />
                <Label 
                  htmlFor="acknowledge-defects" 
                  className="text-sm cursor-pointer leading-tight"
                >
                  Ich bestätige hiermit, dass ich die festgestellten Mängel zur Kenntnis genommen habe
                  {criticalDefectCount > 0 && (
                    <span className="text-red-500 font-medium block mt-1">
                      und dass die {criticalDefectCount} sicherheitsrelevanten Mängel umgehend behoben werden, 
                      um eine behördliche Schliessung des Betriebs zu vermeiden.
                    </span>
                  )}
                </Label>
              </div>
            </div>
          )}
          
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleClear}
              disabled={disabled}
            >
              <Trash2 className="h-3 w-3 mr-1" />
              Löschen
            </Button>
            
            {existingSignature && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setIsEditing(false);
                  setName(existingName || '');
                  setAcknowledged(false);
                }}
              >
                <X className="h-3 w-3 mr-1" />
                Abbrechen
              </Button>
            )}
            
            <Button
              size="sm"
              className="ml-auto bg-green-500 hover:bg-green-600"
              onClick={handleSave}
              disabled={disabled || !name.trim() || (requireAcknowledgement && defectCount > 0 && !acknowledged)}
            >
              <Check className="h-3 w-3 mr-1" />
              Speichern
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return null;
}

export function SignatureSection({ 
  inspection, 
  onSaveSignature, 
  onDeleteSignature,
  disabled = false 
}) {
  // Calculate defect counts
  const items = inspection?.items || [];
  const defectCount = items.filter(i => i.status === 'fail').length;
  const criticalDefectCount = items.filter(i => i.status === 'fail' && i.severity === 'critical').length;
  
  return (
    <div className="space-y-4">
      {/* Critical defects warning */}
      {criticalDefectCount > 0 && (
        <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
          <div className="flex items-start gap-3">
            <ShieldAlert className="h-6 w-6 text-red-500 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-red-500">
                {criticalDefectCount} Sicherheitsrelevante Mängel festgestellt
              </h4>
              <p className="text-sm text-muted-foreground mt-1">
                Diese Mängel müssen <strong>umgehend</strong> behoben werden. Bei Nichtbehebung 
                kann die zuständige Behörde eine <strong>Betriebsschliessung</strong> anordnen.
              </p>
              <p className="text-sm text-red-500 mt-2 font-medium">
                Frist zur Behebung: 30 Tage ab Unterzeichnung
              </p>
            </div>
          </div>
        </div>
      )}
      
      <div className="grid md:grid-cols-2 gap-4">
        <SignaturePad
          title="Prüfer / Abnehmer"
          icon={User}
          existingSignature={inspection?.inspector_signature}
          existingName={inspection?.inspector_signature_name}
          existingDate={inspection?.inspector_signature_date}
          onSave={(signature, name) => onSaveSignature('inspector', signature, name, false)}
          onDelete={() => onDeleteSignature('inspector')}
          disabled={disabled}
        />
        
        <SignaturePad
          title="Kontrollierter Betrieb"
          icon={Building2}
          existingSignature={inspection?.client_signature}
          existingName={inspection?.client_signature_name}
          existingDate={inspection?.client_signature_date}
          existingAcknowledged={inspection?.client_acknowledged_defects}
          onSave={(signature, name, acknowledged) => onSaveSignature('client', signature, name, acknowledged)}
          onDelete={() => onDeleteSignature('client')}
          disabled={disabled}
          requireAcknowledgement={true}
          defectCount={defectCount}
          criticalDefectCount={criticalDefectCount}
        />
      </div>
    </div>
  );
}
